import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "src/components/app/Button";
import Input from "src/components/app/Input";
import Layout from "src/components/app/layout/Layout";

import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as actions from "src/redux/slices/authSlice";
import auth from "src/helpers/auth";
import storage from "src/helpers/storage";

const LoginPage: React.FC = () => {
  const [fields, setFields] = useState({
    mobileNumber: "",
    appVersion: "1.0.0",
    buildNumber: "1",
    platform: "ios",
  });
  const [errors, setErrors] = useState({
    mobileNumber: "",
    appVersion: "",
    buildNumber: "",
    platform: "",
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loginPatient } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // page view
    analytics.pageView(SCREEN_NAMES.LOGIN_SCREEN);

    // logout
    auth.handleLogout();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    switch (fieldName) {
      case "mobileNumber":
        if (!fieldValue.trim()) {
          setFields((prev) => ({ ...prev, mobileNumber: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            mobileNumber: "Mobile number is required",
          }));
        } else if (fieldValue.length <= 10) {
          setFields((prev) => ({ ...prev, mobileNumber: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            mobileNumber:
              fieldValue?.length < 10 ? "Mobile number must be 10 digits" : "",
          }));
        }
        break;

      case "appVersion":
        if (!fieldValue.trim()) {
          setFields((prev) => ({ ...prev, appVersion: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            appVersion: "App version is required",
          }));
        } else {
          setFields((prev) => ({ ...prev, appVersion: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            appVersion: "",
          }));
        }
        break;

      case "buildNumber":
        if (!fieldValue.trim()) {
          setFields((prev) => ({ ...prev, buildNumber: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            buildNumber: "Build number is required",
          }));
        } else {
          setFields((prev) => ({ ...prev, buildNumber: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            buildNumber: "",
          }));
        }
        break;

      case "platform":
        if (!fieldValue.trim()) {
          setFields((prev) => ({ ...prev, platform: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            platform: "Platform is required",
          }));
        } else {
          setFields((prev) => ({ ...prev, platform: fieldValue }));
          setErrors((prev) => ({
            ...prev,
            platform: "",
          }));
        }
        break;

      default:
        break;
    }
  };

  const handleLoginClick = () => {
    const requiredFields = [
      "mobileNumber",
      "appVersion",
      "buildNumber",
      "platform",
    ];
    const errors = requiredFields.filter(
      (field) => !fields[field as keyof typeof fields],
    );
    if (errors.length > 0) {
      setError(`${errors.join(", ")} is required`);
      return;
    }

    if (fields.mobileNumber.length !== 10) {
      setError("Mobile number must be 10 digits");
      return;
    }
    // track event
    analytics.trackEvent(EVENT_NAMES.LOGIN_BUTTON_CLICKED, {
      mobile_number: fields.mobileNumber,
    });

    setError("");

    // login
    handleLogin(fields.mobileNumber);
  };

  const handleLogin = (mobileNumber: string) => {
    const payload = {
      contact_no: mobileNumber,
      source: "PSP",
    };

    new Promise((resolve, reject) => {
      dispatch(
        actions.loginPatientRequest({
          resolve,
          reject,
          payload,
        }),
      );
    })
      .then((res: any) => {
        if (Number(res?.code || 0) === 1) {
          const authToken = res?.data?.token;
          if (authToken) {
            const params = {
              auth_token: authToken,
              app_version: fields.appVersion,
              build_version_number: fields.buildNumber,
              platform: fields.platform,
            };
            const queryString = new URLSearchParams(params).toString();
            navigate(`/app/auth?${queryString}`);
            // actions.getPatientDetailsSuccess(res?.data);
            // storage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);
            // new Promise((resolve, reject) => {
            //   dispatch(
            //     actions.getPatientDetailsRequest({
            //       resolve,
            //       reject,
            //     })
            //   );
            // })
            //   .then((res: any) => {
            //     if (Number(res?.code || 0) === 1) {
            //       analytics.setUserAttributes(res?.data);
            //       const supportProgramKey =
            //         res?.data?.patient_meta_info?.support_program_key;
            //       navigate(`/app/${supportProgramKey}/home-screen`);
            //     }
            //   })
            //   .catch((err: any) => {
            //     console.error("err", err);
            //   });
          }
        } else {
          setError(
            "You are not part of any program. Use GoodFlip App to enjoy the services!",
          );
        }
      })
      .catch((err: any) => {
        console.error("err", err);
      });
  };

  // if (environment?.ENV === "prod") {
  //   return null;
  // }

  return (
    <Layout supportProgramKey="goodflip-care-program" enableTheme={true}>
      <div className="h-full px-4 flex flex-col items-center justify-center">
        <div className="p-4 bg-white flex flex-col items-center justify-center gap-4 rounded-xl max-[90%] w-full">
          {error && <div className="text-red-500">{error}</div>}
          <div className="text-2xl font-bold">Login</div>
          <div className="text-sm text-gray-500">
            Enter your mobile number to login
          </div>
          <Input
            name="mobileNumber"
            type="number"
            placeholder="Enter your mobile number"
            value={fields.mobileNumber}
            onChange={handleChange}
            error={!!errors.mobileNumber}
            helperText={errors.mobileNumber}
            fullWidth
          />

          <Input
            name="appVersion"
            type="text"
            placeholder="1.0.0"
            value={fields.appVersion}
            onChange={handleChange}
            error={!!errors.appVersion}
            helperText={errors.appVersion}
            fullWidth
          />

          <Input
            name="buildNumber"
            type="number"
            placeholder="1"
            value={fields.buildNumber}
            onChange={handleChange}
            error={!!errors.buildNumber}
            helperText={errors.buildNumber}
            fullWidth
          />

          <Input
            name="platform"
            type="text"
            placeholder="android/ios"
            value={fields.platform}
            onChange={handleChange}
            error={!!errors.platform}
            helperText={errors.platform}
            fullWidth
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleLoginClick}
            disabled={fields.mobileNumber?.length !== 10}
          >
            Login
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
